import { Component, Input } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, firstValueFrom, Subject, Subscription } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { CreateTagDialogComponent } from 'src/app/dialogs/create-tag-dialog/create-tag-dialog.component';
import { Organization } from 'src/app/models/organization';
import { Paginator } from 'src/app/models/paginator';
import { Tag } from 'src/app/models/tag';
import { User } from 'src/app/models/user';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { MessageService } from 'src/app/services/message.service';
import { OrganizationService } from 'src/app/services/organization.service';
import { TagService } from 'src/app/services/tag.service';

@Component({
  selector: 'app-organization-tags',
  templateUrl: './organization-tags.component.html',
  styleUrl: './organization-tags.component.scss'
})
export class OrganizationTagsComponent {
  @Input() organization?: Organization;

  loading: boolean = false;
  noDocs: boolean = false;
  done: boolean = false;

  displayedColumns: string[] = ['label', 'description', 'actions'];

  tags: Paginator<Tag> = {
    page: 1,
    limit: 10,
    offset: 0,
    sort: 'label',
    docs: [],
    totalDocs: 0
  }

  public filters: { query?: string } = {
    query: undefined
  }

  private readonly filtersSubject = new Subject<{ query?: string }>();
  private filtersSubscription?: Subscription;

  user?: User;
  me?: User;


  constructor(
    private _message: MessageService,
    private authService: AuthenticationService,
    private dialog: MatDialog,
    private organizationService: OrganizationService,
    private tagService: TagService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {
    this.authService.user.subscribe((user: User | undefined) => {
      this.user = user;
      this.me = user;
    });
  }


  async ngOnInit() {
    this.loading = true;
    this.done = false;
    await this.retrieveTags(true);
    this.loading = false;
    this.done = true;
    this.filtersSubscription = this.filtersSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(async () => {
        this.resetPage();
        await this.retrieveTags(false);
      });
  }


  async retrieveTags(init: boolean) {
    try {
      this._message.emitChange("LOADING", "START");

      this.tags = { ...this.tags, ...await this.tagService.retrieveAll(this.tags.page, this.tags.limit, this.tags.offset, this.filters.query) };

      if (init && this.tags.totalDocs === 0) {
        this.noDocs = true;
      } else if (init && this.tags.totalDocs > 0) {
        this.noDocs = false;
      }

      this._message.emitChange("LOADING", "END");
    } catch (err) {
      console.log(err)
      this._message.emitChange("LOADING", "END");
      this.toastr.error(`ERRORS.GENERIC`)
    }

  }

  resetPage() {
    this.tags.limit = 10;
    this.tags.page = 1;
    this.tags.offset = 0;
  }

  async handlePage(event: any) {
    this.tags.limit = event.pageSize;
    this.tags.page = event.pageIndex + 1;
    this.tags.offset = event.pageIndex * this.tags.limit;

    this.retrieveTags(false);
  }


  async createTag() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        organization: this.organization,
        mode: 'create'
      }
    }
    const dialog = this.dialog.open(CreateTagDialogComponent, config);
    const result: { created: boolean, tag: Tag } = await firstValueFrom(dialog.afterClosed());
    if (result && result.created && result.tag) {
      try {
        await this.tagService.create(result.tag);
        this.toastr.success(this.translate.instant(`ORGANIZATION.CREATE_TAG_SUCCESS`, result.tag))
        this.resetPage();
        await this.retrieveTags(false);
      } catch (err) {
        this.toastr.error('ERRORS.GENERIC');
      }
    }
  }

  async editTag(tag: Tag) {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        organization: this.organization,
        mode: 'edit',
        tag
      }
    }
    const dialog = this.dialog.open(CreateTagDialogComponent, config);
    const result: { edited: boolean, tag: Tag } = await firstValueFrom(dialog.afterClosed());
    if (result && result.edited && result.tag && tag && tag._id) {
      try {
        await this.tagService.update(tag._id, result.tag);
        this.toastr.success(this.translate.instant(`ORGANIZATION.EDIT_TAG_SUCCESS`, tag))
        this.resetPage();
        await this.retrieveTags(false);
      } catch (err) {
        this.toastr.error('ERRORS.GENERIC');
      }
    }
  }

  async deleteTag(tag: Tag) {
    try {
      if (tag && tag._id) {
        this._message.emitChange("LOADING", "START");
        const { count } = await this.tagService.documentsCount(tag._id);
        this._message.emitChange("LOADING", "END");
        const config: MatDialogConfig = {
          panelClass: 'dialog-container',
          width: '480px',
          data: {
            title: 'ORGANIZATION.DELETE_TAG',
            text: this.translate.instant('ORGANIZATION.DELETE_TAG_HINT', { label: tag.label, count }),
            button: {
              text: 'ACTIONS.DELETE',
              class: 'danger-button'
            }
          }
        }
        const dialog = this.dialog.open(ConfirmationDialogComponent, config);
        const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
        if (result && result.confirmed) {
          await this.tagService.delete(tag._id);
          this.resetPage();
          await this.retrieveTags(false);
          this.toastr.success(this.translate.instant(`ORGANIZATION.DELETE_TAG_SUCCESS`, { label: tag.label, count }))
        }
      }

    } catch (err) {
      this._message.emitChange("LOADING", "END");
      this.toastr.error('ERRORS.GENERIC');
    }

  }


}
